/** @jsxImportSource @emotion/react */
import { FC } from 'react'
import { Form } from 'react-final-form'
import { ApolloError } from '@apollo/client'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { BaseModal } from 'components/Modals/BaseModal'
import { InputField } from 'components/Input'
import { SelectField } from 'components/Select'
import { PlatformName, RolesEnum } from '__generatedTypes__/globalTypes'
import { toast } from 'utils/toast'
import { validateEmail, validateRequired } from 'utils/validation'
import { FIND_ALL_CODES, FIND_ONE_INSTITUTIONAL_CODES } from 'queries/institutionalCodes'
import { useCreateCodesMutation, useMemberships } from '../hooks'
import * as classes from './styles'

const DUPLICATE_EMAIL_ERROR_MESSAGE = 'This email address is already registered in this list.'

type Props = {
  open: boolean
  onClose: () => void
  institutionalCodeId?: string
  selectedPlatform?: { id: string; title: PlatformName } | null
  userRole: RolesEnum
  userRoleLabel: string
}

type FormValues = {
  email: string
  membership: { id: string; title: string }
}

export const AddStudentModal: FC<Props> = ({
  open,
  onClose,
  institutionalCodeId,
  selectedPlatform,
  userRole,
  userRoleLabel,
}) => {
  const [createCodes] = useCreateCodesMutation()
  const memberships = useMemberships()

  const handleSubmit = async (values: FormValues) => {
    try {
      const membershipType = memberships.getMembershipTypeFromProduct(values.membership.id)
      const duration = memberships.getDurationFromProduct(values.membership.id)

      await createCodes({
        variables: {
          createCodesInput: {
            userRole,
            email: values.email,
            membershipType,
            duration,
            institutionalCodes: institutionalCodeId ? { id: institutionalCodeId } : null,
          },
        },
        refetchQueries: [FIND_ALL_CODES, FIND_ONE_INSTITUTIONAL_CODES],
        awaitRefetchQueries: true,
      })
      toast.success(`${userRoleLabel} added successfully!`)
      onClose()
    } catch (error: unknown) {
      console.error(`Error adding ${userRoleLabel}:`, error)
      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        const errorMessage = (error.graphQLErrors as ApolloError['graphQLErrors'])?.[0]?.message

        if (errorMessage === DUPLICATE_EMAIL_ERROR_MESSAGE) {
          return { email: errorMessage }
        }
      }
      toast.error('Something went wrong. Please try again.')
    }
  }

  return (
    <BaseModal open={open} onClose={onClose}>
      <div css={classes.headerSection}>
        <Typography css={classes.title} variant="title2">
          New {userRoleLabel}
        </Typography>
      </div>

      <Form<FormValues>
        onSubmit={handleSubmit}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit} css={classes.form}>
            <div css={classes.fieldSection}>
              <InputField name="email" label="Email" placeholder="Enter Email" fullWidth validate={validateEmail} />
            </div>

            <div css={classes.fieldSection}>
              <SelectField
                name="membership"
                label="Membership"
                validate={validateRequired}
                items={selectedPlatform?.title ? memberships.getMembershipItems(selectedPlatform.title) : []}
                placeholder="Select Membership"
                fullWidth
                disabled={!selectedPlatform?.title || memberships.loading}
              />
            </div>

            <div css={classes.fieldSection}>
              <Button
                type="submit"
                variant="contained"
                color="blue"
                size="large"
                fullWidth
                disabled={submitting}
                css={classes.submitButton}
              >
                {submitting ? <CircularProgress size={16} color="inherit" /> : 'Submit'}
              </Button>
            </div>
          </form>
        )}
      />
    </BaseModal>
  )
}
